import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/auth.context'
import { useEffect } from 'react'
import {
  ROUTE_APPLICATION_RESULT,
  ROUTE_EMAIL_VERIFICATION,
  ROUTE_ENFORCE_MFA,
  ROUTE_HOME,
  ROUTE_SIGN_IN,
  ROUTE_SIGN_UP,
  ROUTE_PREQUALIFICATION,
} from './paths'
import { FEATURE_FLAGS } from '@/data/constants'
import {
  EMAIL_VERIFICATION_PROMPT,
  EMAIL_VERIFIED,
  EMAIL_VERIFICATION_EXPIRED,
  CARD_APPLICATION_PENDING,
  CARD_APPLICATION_APPROVED,
  CARD_APPLICATION_STATUSES,
} from '@/data/constants'
import {
  setVerificationDetails,
  persistIntendedRouteWithSearchParams,
  removeIntendedRouteWithSearchParams,
  getIntendedRouteWithSearchParams,
} from '@/storage'
import { confirmSignUp } from 'aws-amplify/auth'
import { useState } from 'react'
import LoadingScreen from '../components/loading-screen'
import PropTypes from 'prop-types'
import Cookies from 'js-cookie'
import { sendAmplitudeEvent } from '../contexts/amplitude.context.jsx'

// eslint-disable-next-line react-refresh/only-export-components
export const usePersistRoute = () => {
  const location = useLocation()

  useEffect(() => {
    // Persist the current route and search params on any route change
    localStorage.setItem('lastRoute', location.pathname)
    localStorage.setItem('lastRouteSearchParams', location.search)
  }, [location])
}

// eslint-disable-next-line react/prop-types
export const RedirectAuthenticated = ({ children }) => {
  const { authenticated, isMFAEnabled, updateMFAStatus, user } = useAuth()
  const cardApplicationState = user?.customer?.cardApplication?.[0]?.status ?? CARD_APPLICATION_PENDING
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (authenticated) {
      const [intendedRoute, intendedRouteSearchParams] = getIntendedRouteWithSearchParams()
      updateMFAStatus() // to re-update MFA status before checking
      if (!user.hasCustomer()) {
        // If the user is authenticated but does not have a customer profile, go to sign-up page
        navigate(ROUTE_SIGN_UP, { replace: true })
      } else if (!isMFAEnabled && cardApplicationState === CARD_APPLICATION_STATUSES[CARD_APPLICATION_APPROVED]) {
        // If the user has a customer profile, card application is approved & MFA is not enabled, redirect to the enforce MFA page
        navigate(ROUTE_ENFORCE_MFA, { replace: true })
      } else if (user.isBannerDismissed()) {
        // If the user has a customer profile, redirect to the last route or home page
        navigate(intendedRoute ? { pathname: intendedRoute, search: intendedRouteSearchParams } : `/${ROUTE_HOME}`, {
          replace: true,
        })
      } else {
        // If the user has a customer profile, redirect to the last route or home page
        navigate(ROUTE_APPLICATION_RESULT, { replace: true })
      }
    } else if (location.pathname !== ROUTE_SIGN_IN) {
      navigate(ROUTE_SIGN_IN, { replace: true })
    } else {
      // If the user is not authenticated, redirect to sign-in page
      navigate(ROUTE_SIGN_IN, { replace: true })
    }
  }, [authenticated, cardApplicationState, isMFAEnabled, navigate, updateMFAStatus, user, location.pathname])

  return authenticated ? null : children
}

// eslint-disable-next-line react/prop-types
export const ProtectedRoute = ({ children }) => {
  const { authenticated, user, isMFAEnabled, loading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  usePersistRoute()

  useEffect(() => {
    const lastRoute = localStorage.getItem('lastRoute')
    const lastRouteSearchParams = localStorage.getItem('lastRouteSearchParams')

    const isInvalidRoute = lastRoute === '/' || lastRoute === ROUTE_SIGN_IN

    if (!user.hasCustomer() && localStorage.getItem('VerificationDetails')) {
      navigate(ROUTE_SIGN_UP)
    } else if (authenticated) {
      if (!user?.isBannerDismissed?.()) {
        navigate(ROUTE_APPLICATION_RESULT, { replace: true })
      } else if (!isInvalidRoute && lastRoute === location.pathname) {
        navigate({ pathname: lastRoute, search: lastRouteSearchParams }, { replace: true })
      } else if (user.isBannerDismissed()) {
        navigate('/' + ROUTE_HOME, { replace: true })
      } else {
        navigate(ROUTE_APPLICATION_RESULT, { replace: true })
      }
    } else {
      persistIntendedRouteWithSearchParams({ pathname: location.pathname, search: location.search })
      navigate(ROUTE_SIGN_IN, { replace: true })
    }
  }, [authenticated, user, navigate, location.pathname, location.search, isMFAEnabled, loading])

  // do not allow bypassMFA if the env variable is not defined
  const bypassMFAAllowed = import.meta.env.VITE_ALLOW_BYPASS_MFA_FOR_TEST_ACCOUNTS === 'true'

  if (
    !isMFAEnabled &&
    user?.isBannerDismissed?.() &&
    !(
      bypassMFAAllowed &&
      user.getCustomer().firstName.startsWith('Fasten') &&
      user.getCustomer().lastName.startsWith('Tester')
    )
  ) {
    // Persist intended route before redirecting to enforce MFA
    setTimeout(() => {
      persistIntendedRouteWithSearchParams(location)
      navigate(ROUTE_ENFORCE_MFA, { replace: true })
    }, 0)
  }

  if (authenticated) {
    if (!user.isBannerDismissed()) {
      // If authenticated and application results banner dismissed
      return <Navigate to={ROUTE_APPLICATION_RESULT} replace />
    } else if (user.hasCustomer()) {
      // If authenticated and application results banner dismissed and user has customer property, User can visit any protected page
      return children
    }
  } else {
    return <Navigate to={ROUTE_SIGN_UP} />
  }
}

// eslint-disable-next-line react/prop-types
export const ProtectedSignUp = ({ children }) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const lastRoute = localStorage.getItem('lastRoute')

  const searchParams = new URLSearchParams(location.search)
  const code = searchParams.get('code')
  const email = searchParams.get('email')
  // For referral-tracking
  const referralTxnId = searchParams.get('transaction_id')
  const ref = searchParams.get('ref')

  const [loading, setLoading] = useState(true) // Added loading state

  useEffect(() => {
    const lastRouteSearchParams = localStorage.getItem('lastRouteSearchParams')
    const handleSignUpConfirmation = async () => {
      if (code && email) {
        try {
          const { isSignUpComplete } = await confirmSignUp({
            username: email,
            confirmationCode: code,
          })

          if (isSignUpComplete) {
            setVerificationDetails({ status: EMAIL_VERIFIED })
            sendAmplitudeEvent(user.userId, 'Email Verified', null, { email_verified_time: new Date().toISOString()})

            navigate(ROUTE_EMAIL_VERIFICATION, { state: EMAIL_VERIFIED, replace: true })
          } else {
            navigate(ROUTE_EMAIL_VERIFICATION, { state: EMAIL_VERIFICATION_PROMPT, replace: true })
          }
        } catch (error) {
          if (error.message === 'Invalid code provided, please request a code again.') {
            setVerificationDetails({ status: EMAIL_VERIFICATION_EXPIRED })
            navigate(ROUTE_EMAIL_VERIFICATION, { state: EMAIL_VERIFICATION_EXPIRED, replace: true })
          } else {
            console.error(error)
          }
        } finally {
          setLoading(false) // Set loading to false after the process is done
        }
      } else if (ref) {
        // TODO: Handle if user is on application submission stage and again coming from another ref, ...etc scenarios
        // Overwrite the cookie if it already exists (last one wins).
        // NOTE: Testings: we need to append `&source=testoffer&aff_sub=testoffer` to the referral link
        const refObj = {
          source: ref, // ref===33 means, Tune
          path: '/sign-up',
          parameters: {
            transaction_id: referralTxnId,
          },
        }
        const jsonString = JSON.stringify(refObj)
        const base64Str = btoa(jsonString)
        Cookies.set('refTracking', base64Str, { expires: 30, secure: true, path: '/' })
        setLoading(false)
      } else {
        if (!user.hasCustomer()) {
          // Either user hasn't submit card application or user hasn't signed up using email,PW
          // Preserve query parameters and state when navigating to sign-up
          navigate({ pathname: ROUTE_SIGN_UP, search: location.search }, { replace: true, state: location.state })
          // If first banner dismissed only user can visit home or routes within the app
        } else if (user?.isBannerDismissed?.()) {
          // User already has a customer profile
          navigate(lastRoute ? { pathname: lastRoute, search: lastRouteSearchParams } : `/${ROUTE_HOME}`, {
            replace: true,
          })
        } else {
          // If not we need to show application results page
          navigate(ROUTE_APPLICATION_RESULT, { replace: true })
        }
        setLoading(false) // Set loading to false when not confirming sign-up
      }
    }

    handleSignUpConfirmation()
  }, [lastRoute, navigate, user, code, email, referralTxnId, ref, location.search, location.state])

  // Show a loading spinner or null while loading
  if (loading) {
    return <LoadingScreen />
  }

  return user.hasCustomer() ? null : children
}

export const ProtectedApplicationResult = ({ children }) => {
  const { authenticated, user, isCardApplicationSubmitted } = useAuth()
  const navigate = useNavigate()
  useEffect(() => {
    if (!authenticated) {
      navigate(ROUTE_SIGN_IN, { replace: true })
      // If user already dismissed banner, it means he is already visited home and it's better to not to show application results page
    } else if (user?.isBannerDismissed?.()) {
      navigate('/' + ROUTE_HOME, { replace: true })
    } else if (!user?.hasCustomer?.() && !isCardApplicationSubmitted) {
      // If user has no customer profile, it means he is not submitted card application yet and it's better to not to show application results page
      navigate(ROUTE_SIGN_UP, { replace: true })
    }
  }, [authenticated, navigate, user, isCardApplicationSubmitted])

  return !authenticated ? ROUTE_SIGN_IN : children
}

export const ProtectedEnforceMFA = ({ children }) => {
  const { authenticated, isMFAEnabled, loading, isPhoneNumberVerified, updatePhoneNumberStatus, updateMFAStatus } =
    useAuth()
  const navigate = useNavigate()
  useEffect(() => {
    updatePhoneNumberStatus()
    if (!isMFAEnabled || !isPhoneNumberVerified) {
      navigate(ROUTE_ENFORCE_MFA, { replace: true })
    } else {
      const [intendedRoute, intendedRouteSearchParams] = getIntendedRouteWithSearchParams()
      // if intendedRoute exist go to that path with search params, otherwise go to /home
      navigate(intendedRoute ? { pathname: intendedRoute, search: intendedRouteSearchParams } : `/${ROUTE_HOME}`, {
        replace: true,
      })
      removeIntendedRouteWithSearchParams()
    }
  }, [authenticated, isMFAEnabled, isPhoneNumberVerified, navigate, updateMFAStatus, updatePhoneNumberStatus])

  if (loading) {
    return <LoadingScreen />
  }
  return !isMFAEnabled || !isPhoneNumberVerified ? children : null
}

ProtectedEnforceMFA.propTypes = {
  children: PropTypes.node.isRequired,
}

export const ProtectedPrequalificationRoute = ({ 
  children,
  requirePrequalData = false,  // For pages needing prequalData
  requireReason = false        // For pages needing reason
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [isValidating, setIsValidating] = useState(true)
  
  useEffect(() => {
    const hasPrequalData = !!location.state?.prequalData
    const hasReason = !!location.state?.reason
    
    let isValid = false
    
    if (requirePrequalData && hasPrequalData) {
      isValid = true
    } else if (requireReason && hasReason) {
      isValid = true
    } else if (!requirePrequalData && !requireReason) {
      // No requirements - allow access
      isValid = true
    }
    
    if (!isValid) {
      // Redirect to main form if state is missing
      navigate(ROUTE_PREQUALIFICATION, { replace: true })
    } else {
      setIsValidating(false)
    }
  }, [location.state, requirePrequalData, requireReason, navigate])
  
  if (isValidating) {
    return <LoadingScreen />
  }
  
  return children
}

ProtectedPrequalificationRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requirePrequalData: PropTypes.bool,
  requireReason: PropTypes.bool,
}

/**
 * Feature flag wrapper for prequalification routes.
 * Redirects to sign-up if the prequalification feature is disabled.
 */
export const PrequalificationFeatureFlag = ({ children }) => {
  if (!FEATURE_FLAGS.ENABLE_PREQUALIFICATION) {
    return <Navigate to={ROUTE_SIGN_UP} replace />
  }

  return children
}

PrequalificationFeatureFlag.propTypes = {
  children: PropTypes.node.isRequired,
}
